import React, { useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { sendExistingCustomerOTP, verifyCustomerOTP, pb } from '../api-utils'
import Spinner from './Spinner'
import SessionExpiredModal from './SessionExpiredModal'

interface LoginModalProps {
    isOpen: boolean
    onClose: () => void
    onLoginSuccess?: (user: any) => void
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
    const [step, setStep] = useState<'email' | 'verify'>('email')
    const [otpId, setOtpId] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [successMessage, setSuccessMessage] = useState<string>('')
    const [showInactiveModal, setShowInactiveModal] = useState(false)

    if (!isOpen) return null

    const handleRequestOTP = async (values: { email: string }) => {
        setIsLoading(true)
        setErrorMessage('')
        setSuccessMessage('')

        try {
            const response = await sendExistingCustomerOTP(values.email)

            if (response.success) {
                setOtpId(response.otpId)
                setEmail(values.email)
                setStep('verify')
                setSuccessMessage(response.message || 'OTP code sent to your email. Please check your inbox.')
            } else {
                setErrorMessage(response.error || 'Failed to send OTP')
            }
        } catch (error: any) {
            console.error('Error requesting OTP:', error)
            setErrorMessage(error?.response?.data?.error || 'Failed to send OTP. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleVerifyOTP = async (values: { otpCode: string }) => {
        setIsLoading(true)
        setErrorMessage('')
        // Keep the success message from the OTP send step

        try {
            const response = await verifyCustomerOTP(email, values.otpCode, otpId)

            if (response.success) {
                // Store the auth token in PocketBase
                pb.authStore.save(response.token, response.user)

                setSuccessMessage('Login successful!')

                // Call the success callback if provided
                if (onLoginSuccess) {
                    onLoginSuccess(response.user)
                }

                // Close modal after a brief delay
                setTimeout(() => {
                    handleClose()
                }, 1000)
            } else if (response.inactive) {
                // Account is not active - reset form and show inactive account modal
                setStep('email')
                setOtpId('')
                setEmail('')
                setErrorMessage('')
                setSuccessMessage('')
                setShowInactiveModal(true)
            } else {
                setErrorMessage(response.error || 'Invalid OTP code')
            }
        } catch (error: any) {
            console.error('Error verifying OTP:', error)

            // Check if the error is due to inactive account
            if (error?.response?.data?.inactive) {
                // Account is not active - reset form and show inactive account modal
                setStep('email')
                setOtpId('')
                setEmail('')
                setErrorMessage('')
                setSuccessMessage('')
                setShowInactiveModal(true)
            } else {
                setErrorMessage(error?.response?.data?.error || 'Invalid or expired OTP code. Please try again.')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        setStep('email')
        setOtpId('')
        setEmail('')
        setErrorMessage('')
        setSuccessMessage('')
        onClose()
    }

    const emailValidationSchema = Yup.object({
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
    })

    const otpValidationSchema = Yup.object({
        otpCode: Yup.string()
            .required('One Time Password is required')
            .length(6, 'One Time Password must be 6 digits')
    })

    return (
        <div className="modal is-active">
            <div className="modal-background" onClick={handleClose}></div>
            <div className="modal-card" style={{ maxWidth: '500px' }}>
                <div style={{
                    background: '#232043',
                    borderRadius: '18px 18px 0 0',
                    padding: '1.25rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{
                        color: '#fdfa52',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        margin: 0
                    }}>
                        {step === 'email' ? 'Customer Login' : 'Verify One Time Password'}
                    </h2>
                    <span
                        aria-label="close"
                        onClick={() => !isLoading && handleClose()}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '20px',
                            height: '20px',
                            padding: 0,
                            margin: 0,
                            opacity: isLoading ? 0.5 : 1,
                            boxShadow: 'none',
                            outline: 'none',
                            flexShrink: 0
                        }}
                    >
                        <span style={{
                            display: 'block',
                            width: '16px',
                            height: '2px',
                            background: '#fdfa52',
                            transform: 'rotate(45deg)',
                            position: 'absolute'
                        }}></span>
                        <span style={{
                            display: 'block',
                            width: '16px',
                            height: '2px',
                            background: '#fdfa52',
                            transform: 'rotate(-45deg)',
                            position: 'absolute'
                        }}></span>
                    </span>
                </div>

                <section className="modal-card-body" style={{
                    background: 'linear-gradient(to right, #fdfa52, #FFAE42)',
                    borderRadius: '0 0 18px 18px'
                }}>
                    {errorMessage && (
                        <div style={{
                            background: '#232043',
                            color: '#fdfa52',
                            padding: '0.75rem 1rem',
                            marginBottom: '1rem',
                            borderRadius: '6px',
                            fontSize: '0.9rem'
                        }}>
                            {errorMessage}
                        </div>
                    )}

                    {successMessage && (
                        <div style={{
                            background: '#232043',
                            color: '#fdfa52',
                            padding: '0.75rem 1rem',
                            marginBottom: '1rem',
                            borderRadius: '6px',
                            fontSize: '0.9rem'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    {step === 'email' ? (
                        <Formik
                            initialValues={{ email: '' }}
                            validationSchema={emailValidationSchema}
                            onSubmit={handleRequestOTP}
                        >
                            {({ errors, touched }) => (
                                <Form>
                                    <div className="field">
                                        <label className="label">Email Address</label>
                                        <div className="control has-icons-left" style={{ maxWidth: '300px', margin: '0 auto' }}>
                                            <Field
                                                name="email"
                                                type="email"
                                                className={`input ${errors.email && touched.email ? 'is-danger' : ''}`}
                                                placeholder="your@email.com"
                                                disabled={isLoading}
                                                style={{
                                                    letterSpacing: 0,
                                                    fontSize: '1.2rem',
                                                    textAlign: 'center'
                                                }}
                                            />
                                            <span className="icon is-small is-left" style={{ fontSize: '1.2rem' }}>
                                                <i className="fa fa-envelope"></i>
                                            </span>
                                        </div>
                                        {errors.email && touched.email && (
                                            <p className="help is-danger" style={{ textAlign: 'center' }}>{errors.email}</p>
                                        )}
                                    </div>

                                    <div className="field">
                                        <div className="control" style={{ display: 'flex', justifyContent: 'center' }}>
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                style={{ background: '#232043', color: 'yellow' }}
                                            >
                                                {isLoading ? <Spinner /> : <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Request OTP Code</span>}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="content is-small has-text-centered">
                                        <p style={{ color: '#666', marginTop: '1rem' }}>
                                            A one-time password will be sent to your email address.
                                        </p>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        <Formik
                            initialValues={{ otpCode: '' }}
                            validationSchema={otpValidationSchema}
                            onSubmit={handleVerifyOTP}
                        >
                            {({ errors, touched }) => (
                                <Form>
                                    <div className="forceBlack content is-small" style={{ marginBottom: '1.5rem' }}>
                                        <p>
                                            You entered email: <strong>{email}</strong>
                                        </p>
                                        {/* <p>
                                            <button
                                                type="button"
                                                className="button is-text"
                                                onClick={() => {
                                                    setStep('email')
                                                    setSuccessMessage('')
                                                }}
                                                disabled={isLoading}
                                            >
                                                Use a different email
                                            </button>
                                        </p> */}
                                    </div>

                                    <div className="field">
                                        <label className="label">Enter Verification Code</label>
                                        <div className="control has-icons-left" style={{ maxWidth: '200px', margin: '0 auto' }}>
                                            <Field
                                                name="otpCode"
                                                type="tel"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className={`input ${errors.otpCode && touched.otpCode ? 'is-danger' : ''}`}
                                                placeholder="000000"
                                                maxLength={6}
                                                disabled={isLoading}
                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (!/[0-9]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                style={{
                                                    letterSpacing: 0,
                                                    fontFamily: 'monospace',
                                                    fontSize: '1.2rem',
                                                    textAlign: 'center'
                                                }}
                                            />
                                            <span className="icon is-small is-left" style={{ fontSize: '1.2rem', height: '100%', display: 'flex', alignItems: 'center' }}>
                                                <i className="fa fa-key"></i>
                                            </span>
                                        </div>
                                        {errors.otpCode && touched.otpCode && (
                                            <p className="help is-danger" style={{ textAlign: 'center' }}>{errors.otpCode}</p>
                                        )}
                                    </div>

                                    <div className="field">
                                        <div className="control" style={{ display: 'flex', justifyContent: 'center' }}>
                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                style={{ background: '#232043', color: 'yellow' }}
                                            >
                                                {isLoading ? <Spinner /> : <span style={{ fontWeight: 700, letterSpacing: 1.2 }}>Verify & Login</span>}
                                            </button>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    )}
                </section>
            </div>

            {/* Inactive Account Modal */}
            <SessionExpiredModal
                isOpen={showInactiveModal}
                onClose={() => {
                    setShowInactiveModal(false)
                    onClose() // Close the parent login modal
                }}
                title="User Not Active"
                message={
                    <>
                        Your account is not active in our system. Please contact us{' '}
                        <a
                            href="tel:614-699-2495"
                            style={{
                                color: '#232043',
                                fontWeight: 700,
                                textDecoration: 'underline'
                            }}
                        >
                            (614) 699-2495
                        </a>
                    </>
                }
                buttonMode="close-only"
            />
        </div>
    )
}

export default LoginModal
